// Icon search for the admin picker. Runs entirely in the browser using the
// static catalog (public/icon-catalog.json); no AI or network calls.
//
// Ranking: exact and prefix matches on the icon name come first, then
// official tags, then synonyms. If that finds few results, typo-tolerant
// matching (Fuse.js) fills in the rest.
import Fuse from "fuse.js";

export interface CatalogIcon {
  f: string; // file name in /icons
  n: string; // display name
  s: "ph" | "ms"; // source set
  t: string[]; // official tags
  y: string[]; // synonyms (one-time AI generated, hand-editable)
  th: string[]; // themes
}

export interface SearchOptions {
  theme?: string | null;
  limit?: number;
}

const norm = (s: string) => s.toLowerCase().trim().replace(/[-_]+/g, " ").replace(/\s+/g, " ");

// Very light plural handling so "cats" finds "cat" and "berries" finds "berry"
const stems = (w: string): string[] => {
  const out = [w];
  if (w.length > 4 && w.endsWith("ies")) out.push(w.slice(0, -3) + "y");
  else if (w.length > 3 && w.endsWith("es")) out.push(w.slice(0, -2), w.slice(0, -1));
  else if (w.length > 3 && w.endsWith("s")) out.push(w.slice(0, -1));
  return out;
};

interface Prepared {
  icon: CatalogIcon;
  name: string;
  nameWords: string[];
  tags: string[];
  syns: string[];
  uiOnly: boolean;
}

const scoreTerm = (p: Prepared, term: string): number => {
  let best = 0;
  for (const q of stems(term)) {
    const long = q.length >= 3;
    if (p.name === q) best = Math.max(best, 1000);
    if (p.nameWords.includes(q)) best = Math.max(best, 600);
    if (p.tags.includes(q)) best = Math.max(best, 400);
    if (p.syns.includes(q)) best = Math.max(best, 350);
    if (long && p.nameWords.some((w) => w.startsWith(q))) best = Math.max(best, 300);
    if (long && p.tags.some((w) => w.startsWith(q))) best = Math.max(best, 150);
    if (long && p.syns.some((w) => w.startsWith(q) || w.split(" ").some((x) => x.startsWith(q)))) best = Math.max(best, 120);
    if (q.length >= 4 && p.name.includes(q)) best = Math.max(best, 80);
  }
  return best;
};

export class IconSearch {
  private prepared: Prepared[];
  private fuse: Fuse<Prepared>;

  constructor(catalog: CatalogIcon[]) {
    this.prepared = catalog.map((icon) => {
      const name = norm(icon.n);
      return {
        icon,
        name,
        nameWords: name.split(" "),
        tags: icon.t.map(norm),
        syns: icon.y.map(norm),
        uiOnly: icon.th.length > 0 && icon.th.every((t) => t === "Arrows & Interface"),
      };
    });
    this.fuse = new Fuse(this.prepared, {
      keys: [
        { name: "name", weight: 3 },
        { name: "tags", weight: 2 },
        { name: "syns", weight: 1.5 },
      ],
      threshold: 0.25,
      ignoreLocation: true,
      minMatchCharLength: 3,
    });
  }

  search(query: string, opts: SearchOptions = {}): CatalogIcon[] {
    const { theme = null, limit = Infinity } = opts;
    const inTheme = (p: Prepared) => !theme || p.icon.th.includes(theme);
    const q = norm(query);

    if (!q) {
      return this.prepared.filter(inTheme).map((p) => p.icon).slice(0, limit);
    }

    // 1. Keyword matches: every word in the query must match something
    const terms = q.split(" ");
    const scored: { p: Prepared; score: number }[] = [];
    for (const p of this.prepared) {
      if (!inTheme(p)) continue;
      let total = 0;
      let all = true;
      for (const term of terms) {
        const s = scoreTerm(p, term);
        if (!s) { all = false; break; }
        total += s;
      }
      // whole multi-word phrase as a name, tag or synonym (e.g. "ice cream")
      if (terms.length > 1 && (p.tags.includes(q) || p.syns.includes(q) || p.name === q)) {
        total += 500;
        all = true;
      }
      if (all && total > 0) {
        // Puzzle themes rarely need app-interface glyphs (alignment, toggles...),
        // so rank them below themed icons unless they are an exact name match
        if (p.uiOnly && p.name !== q) total *= 0.5;
        scored.push({ p, score: total });
      }
    }
    // Ties: shorter (simpler) names first, then alphabetical
    scored.sort((a, b) => b.score - a.score || a.p.name.length - b.p.name.length || a.p.name.localeCompare(b.p.name));
    const results = scored.map((x) => x.p.icon);

    // 2. Typo-tolerant fallback, only when keyword matching finds almost nothing
    if (results.length < 3 && q.length >= 3) {
      const seen = new Set(results.map((r) => r.f));
      for (const r of this.fuse.search(q, { limit: 40 })) {
        if (!seen.has(r.item.icon.f) && inTheme(r.item)) {
          results.push(r.item.icon);
          seen.add(r.item.icon.f);
        }
      }
    }
    return results.slice(0, limit);
  }
}

export const THEMES = [
  "Food & Drink", "Animals", "Nature & Plants", "Weather & Sky", "Sports & Fitness", "Games & Toys",
  "Travel & Places", "Transport", "Home & Household", "Clothing & Accessories", "People & Body",
  "Health & Medical", "Music & Audio", "Art & Media", "Technology & Devices", "Work & Money",
  "School & Science", "Tools & Building", "Holidays & Celebrations", "Communication",
  "Symbols & Shapes", "Arrows & Interface", "Brands & Logos",
];
